import { getPagosDeuda, moneyNumber } from "./finanzasDeuda";
import { safeMoneyNumber } from "./contextoFinanciero";
import {
  esPropiedadVendida,
  generaUtilidadKye,
  getPrecioContadoPropiedad,
} from "./compatFinanciera";

export const getPagosPositivos = (deuda = {}) =>
  getPagosDeuda(deuda).filter((p) => moneyNumber(p?.cantidad) > 0);

// Detecta deudas canceladas / en devolución / inactivas que NO deben
// contribuir al ingreso ni al cobrado de la propiedad en el ERP.
const esDeudaInvalidada = (d = {}) => {
  if (!d) return true;
  if (d.activo === false) return true;
  if (d.devolucion === true) return true;
  const estado = String(d.estado || "").toLowerCase();
  const estatus = String(d.estatus || "").toLowerCase();
  if (estado === "cancelada" || estado.includes("cancel")) return true;
  if (estatus.includes("cancel") || estatus.includes("cerrad")) return true;
  if (estado.includes("devolucion") || estado === "devuelta") return true;
  return false;
};

export const calcularFinanzasPropiedad = ({
  propiedad = {},
  deudas = [],
  gastosDirectos = [],
  costosProrrateados = 0,
} = {}) => {
  // Elegir la deuda VIGENTE (no cancelada / no inactiva / no en devolución).
  // Si la propiedad fue revendida tras cancelación, este filtro selecciona
  // la deuda actual real y descarta las históricas canceladas.
  const deudasVigentes = (deudas || []).filter((d) => !esDeudaInvalidada(d));
  const deudaPrincipal = deudasVigentes[0] || null;
  const pagos = deudaPrincipal ? getPagosPositivos(deudaPrincipal) : [];
  const generaUtilidad = generaUtilidadKye(propiedad);
  const vendida = esPropiedadVendida(propiedad);

  const ingresoBase = safeMoneyNumber(
    deudaPrincipal?.monto ??
      deudaPrincipal?.importe ??
      propiedad?.precioVenta ??
      propiedad?.precioFinanciado ??
      propiedad?.precioContado
  );
  const ingresoOperado = vendida ? ingresoBase : 0;
  const ingresoVendido = generaUtilidad ? ingresoOperado : 0;
  const ingresoProyectadoOperado = vendida
    ? ingresoOperado
    : getPrecioContadoPropiedad(propiedad);
  const ingresoProyectado = generaUtilidad ? ingresoProyectadoOperado : 0;

  const totalCobradoOperado = pagos.reduce(
    (acc, pago) => acc + safeMoneyNumber(pago?.cantidad),
    0
  );
  const totalCobrado = generaUtilidad ? totalCobradoOperado : 0;

  const saldoPendienteOperado = Math.max(ingresoOperado - totalCobradoOperado, 0);
  const saldoPendiente = generaUtilidad ? saldoPendienteOperado : 0;

  const costosDirectos = gastosDirectos.reduce(
    (acc, gasto) => acc + safeMoneyNumber(gasto?.importe),
    0
  );

  const costosDirectosCapitalizables = gastosDirectos
    .filter((gasto) => gasto.capitalizable === true)
    .reduce((acc, gasto) => acc + safeMoneyNumber(gasto?.importe), 0);

  const costosDirectosNoCapitalizables = Math.max(
    costosDirectos - costosDirectosCapitalizables,
    0
  );

  const utilidadProyectada = ingresoProyectado - costosDirectosCapitalizables;
  const utilidadRealizada = totalCobrado - costosDirectosCapitalizables;
  const costoTotalAsignado = costosDirectosCapitalizables + safeMoneyNumber(costosProrrateados);
  const utilidadProyectadaConProrrateo = ingresoProyectado - costoTotalAsignado;
  const utilidadRealizadaConProrrateo = totalCobrado - costoTotalAsignado;
  const margenProyectado =
    ingresoProyectado > 0 ? utilidadProyectada / ingresoProyectado : null;
  const margenConProrrateo =
    ingresoProyectado > 0 ? utilidadProyectadaConProrrateo / ingresoProyectado : null;

  return {
    deudaPrincipal,
    pagos,
    generaUtilidadKye: generaUtilidad,
    ingresoOperado,
    ingresoVendido,
    ingresoProyectadoOperado,
    ingresoProyectado,
    totalCobradoOperado,
    totalCobrado,
    saldoPendienteOperado,
    saldoPendiente,
    costosDirectos,
    costosDirectosCapitalizables,
    costosDirectosNoCapitalizables,
    costosProrrateados: safeMoneyNumber(costosProrrateados),
    costoTotalAsignado,
    utilidadProyectada,
    utilidadRealizada,
    utilidadProyectadaConProrrateo,
    utilidadRealizadaConProrrateo,
    margenProyectado,
    margenConProrrateo,
    gastosDirectosCount: gastosDirectos.length,
  };
};

export const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  return `${Math.round(Number(value) * 1000) / 10}%`;
};
